import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, CardHeader, Container, Grid, MenuItem, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { Page } from '../../../components';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import { getAuditBranches } from '../../branches/store/branchActions';
import { getAuditDepartments } from '../../departments/store/departmentActions';
import { createStaff } from '../store/userActions';

// ----------------------------------------------------------------------

const ROLES = [
  { id: 1, label: 'Admin', value: 'admin' },
  { id: 2, label: 'Finance', value: 'finance' },
  { id: 3, label: 'Operations', value: 'operations' },
  { id: 4, label: 'IT', value: 'it' },
  { id: 5, label: 'Auditor', value: 'auditor' },
];

export default function NewUserForm() {
  const dispatch = useDispatch();
  const { auditBranches } = useSelector((state) => state.branchReducer);
  const { auditDepartments } = useSelector((state) => state.departmentReducer);
  const { isSuccess, isLoading } = useSelector((state) => state.userReducer);
  const navigate = useNavigate();

  const NewUserSchema = Yup.object().shape({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string()
      .required('Email is required')
      .email('Enter a valid email address')
      .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Please enter a valid email format')
      .lowercase('Email must be in lowercase'),
    phoneNumber: Yup.string()
      .required('Phone number is required')
      .matches(/^\d+$/, 'Phone number must contain only numbers')
      .min(10, 'Phone number must be at least 10 digits')
      .max(15, 'Phone number cannot exceed 15 digits'),
    idNumber: Yup.string(),
    staffNumber: Yup.string().required('Staff Number is required'),
    department: Yup.string().required('Department is required'),
    branch: Yup.string().required('Location is required'),
    role: Yup.string().required('Role Number is required'),
  });

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    idNumber: '',
    staffNumber: '',
    department: '',
    branch: '',
    role: '',
  };

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
    mode: 'onChange', // Enable real-time validation
    reValidateMode: 'onChange',
  });

  const {
    reset,
    watch,
    // control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const emailValue = watch('email');

  useEffect(() => {
    dispatch(getAuditBranches({}));
    dispatch(getAuditDepartments());
  }, [dispatch]);

  // Auto-transform email to lowercase
  useEffect(() => {
    if (emailValue && emailValue !== emailValue.toLowerCase()) {
      setValue('email', emailValue.toLowerCase(), { shouldValidate: true });
    }
  }, [emailValue, setValue]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        email: data.email.toLowerCase().trim(),
        id_number: data.idNumber,
        mobile_number: data.phoneNumber.trim(),
        staff_number: data.staffNumber.trim(),
        department: data.department.trim(),
        role: data.role,
        location_id: data.branch,
      };
      dispatch(createStaff(payload));
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (isSuccess) {
      reset();
      navigate('/dashboard/people');
    }
  }, [reset, isSuccess, navigate]);
  return (
    <Page title="Users">
      <Container maxWidth="xl">
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <Card sx={{ p: 3 }}>
                <CardHeader title={'Create user'} subheader={''} />
                <CardContent>
                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField name="firstName" label="First Name" />
                    <RHFTextField name="lastName" label="Last Name" />
                    <RHFTextField
                      type="email"
                      name="email"
                      label="Email Address"
                      placeholder="user@example.com"
                      autoComplete="email"
                    />
                    <RHFTextField
                      name="phoneNumber"
                      label="Phone Number"
                      placeholder="07XXXXXXXX"
                      autoComplete="tel"
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        onKeyPress: (event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        },
                      }}
                    />
                    {/* <RHFTextField
                      name="idNumber"
                      label="ID Number"
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        onKeyPress: (event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        },
                      }}
                    /> */}
                    <RHFTextField name="staffNumber" label="Staff Number" />
                    <RHFSelect
                      name="department"
                      label="Department"
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                    >
                      {auditDepartments?.map((dept) => (
                        <MenuItem key={dept.id} value={dept.name}>
                          {dept.name}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                    <RHFSelect
                      name="branch"
                      label="Location"
                      placeholder="Location"
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                    >
                      {auditBranches?.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                    <RHFSelect
                      fullWidth
                      name="role"
                      label="Role"
                      placeholder="Role"
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                    >
                      {ROLES.map((option) => (
                        <MenuItem key={option.id} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Box>

                  <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                    <LoadingButton type="submit" variant="contained" disabled={isSubmitting} loading={isLoading}>
                      Create User
                    </LoadingButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </FormProvider>
      </Container>
    </Page>
  );
}
